/**
 * @module features/display-module/components/DdmArtifactFormModal
 * @description Two-step responsive creation & editing modal for all 7 DDM Display Artifact
 * types with live character counters, strict `Image` XOR `Video` mutual exclusion, and
 * client-side file size (`4 MB` / `15 MB`) & HTML5 `<video>` duration (`15s` / `5s`) checks (`T016`).
 */

import * as React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  AlertCircle,
  Loader2,
  MonitorSmartphone,
  Upload,
  X,
  Image as ImageIcon,
  Video as VideoIcon,
  Ban,
  Megaphone,
  Cake,
  AlertTriangle,
  Quote,
  Images,
  Sparkles,
  CalendarDays,
  CheckCircle2,
} from "lucide-react";
import { cn } from "@/lib/utils";
import {
  DDM_ARTIFACT_TYPES,
  DDM_ARTIFACT_TYPE_META,
  MAX_DISPLAY_DURATION_SECONDS,
  MAX_GALLERY_IMAGES,
  MAX_QUOTE_VIDEO_DURATION_SECONDS,
  MAX_VIDEO_DURATION_SECONDS,
  validateDdmFormSubmission,
  validateImageFileClientSide,
  validateVideoFileClientSide,
  type AssignedDdmDeviceSummary,
  type DdmArtifactType,
  type DdmNotificationFormValues,
  type DdmNotificationRecord,
} from "../types/ddm";
import type { CreateOrUpdateDdmArtifactInput } from "../api/ddmApi";

interface DdmArtifactFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  devices: AssignedDdmDeviceSummary[];
  editRecord?: DdmNotificationRecord | null;
  defaultDeviceId?: string;
  onSubmit: (
    input: CreateOrUpdateDdmArtifactInput,
    editId?: string
  ) => Promise<void>;
  isSubmitting: boolean;
}

const TYPE_ICONS: Record<
  DdmArtifactType,
  React.ComponentType<{ className?: string }>
> = {
  notice: Megaphone,
  birthday: Cake,
  emergency_alert: AlertTriangle,
  quote: Quote,
  gallery: Images,
  custom_banner: Sparkles,
  event: CalendarDays,
};

function toLocalDateTimeInputValue(iso?: string): string {
  const date = iso ? new Date(iso) : new Date();
  if (Number.isNaN(date.getTime())) {
    return new Date().toISOString().slice(0, 16);
  }
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate()
  )}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function defaultStartEnd(): { start: string; end: string } {
  const now = new Date();
  const later = new Date(now.getTime() + 24 * 60 * 60 * 1000);
  return {
    start: toLocalDateTimeInputValue(now.toISOString()),
    end: toLocalDateTimeInputValue(later.toISOString()),
  };
}

export const DdmArtifactFormModal: React.FC<DdmArtifactFormModalProps> = ({
  open,
  onOpenChange,
  devices,
  editRecord,
  defaultDeviceId,
  onSubmit,
  isSubmitting,
}) => {
  const isEdit = Boolean(editRecord);
  const [step, setStep] = React.useState<1 | 2>(1);
  const [errorMsg, setErrorMsg] = React.useState<string | null>(null);

  const [imageFile, setImageFile] = React.useState<File | null>(null);
  const [videoFile, setVideoFile] = React.useState<File | null>(null);
  const [galleryFiles, setGalleryFiles] = React.useState<File[]>([]);
  const [probedVideoDuration, setProbedVideoDuration] = React.useState<
    number | null
  >(null);

  const [values, setValues] = React.useState<DdmNotificationFormValues>(() => {
    const { start, end } = defaultStartEnd();
    return {
      device_id: defaultDeviceId || devices[0]?.device_id || "",
      artifact_type: "notice",
      start_timestamp: start,
      end_timestamp: end,
      duration_of_display: 15,
      per_cycle_display_count: 1,
      pause_emergency_alert: false,
      remarks: "",
      title: "",
      body: "",
      group_title: "",
      event_date: new Date().toISOString().slice(0, 10),
      media_mode: "none",
      image_url: "",
      image_urls: [],
      video_url: "",
      video_duration_seconds: null,
    };
  });

  // Reset form state whenever modal opens or editRecord changes
  React.useEffect(() => {
    if (!open) return;
    setErrorMsg(null);
    setImageFile(null);
    setVideoFile(null);
    setGalleryFiles([]);
    setProbedVideoDuration(null);

    if (editRecord) {
      setStep(2);
      const cp = editRecord.content_payload ?? {};
      const hasVid = Boolean(cp.video_url);
      const hasImg =
        Boolean(cp.image_url) ||
        (Array.isArray(cp.image_urls) && cp.image_urls.length > 0);

      setValues({
        device_id: editRecord.device_id,
        artifact_type: editRecord.artifact_type,
        start_timestamp: toLocalDateTimeInputValue(editRecord.start_timestamp),
        end_timestamp: toLocalDateTimeInputValue(editRecord.end_timestamp),
        duration_of_display: editRecord.duration_of_display ?? 15,
        per_cycle_display_count: editRecord.per_cycle_display_count ?? 1,
        pause_emergency_alert: Boolean(editRecord.pause_emergency_alert),
        remarks: editRecord.remarks ?? "",
        title: cp.title ?? "",
        body: cp.body ?? "",
        group_title: cp.group_title ?? "",
        event_date:
          editRecord.event_date ??
          cp.event_date ??
          new Date().toISOString().slice(0, 10),
        media_mode: hasVid ? "video" : hasImg ? "image" : "none",
        image_url: cp.image_url ?? "",
        image_urls: Array.isArray(cp.image_urls) ? [...cp.image_urls] : [],
        video_url: cp.video_url ?? "",
        video_duration_seconds: cp.video_duration_seconds ?? null,
      });
    } else {
      setStep(1);
      const { start, end } = defaultStartEnd();
      setValues({
        device_id:
          defaultDeviceId && defaultDeviceId !== "all"
            ? defaultDeviceId
            : devices[0]?.device_id || "",
        artifact_type: "notice",
        start_timestamp: start,
        end_timestamp: end,
        duration_of_display: 15,
        per_cycle_display_count: 1,
        pause_emergency_alert: false,
        remarks: "",
        title: "",
        body: "",
        group_title: "",
        event_date: new Date().toISOString().slice(0, 10),
        media_mode: "none",
        image_url: "",
        image_urls: [],
        video_url: "",
        video_duration_seconds: null,
      });
    }
  }, [open, editRecord, defaultDeviceId, devices]);

  const currentMeta = DDM_ARTIFACT_TYPE_META[values.artifact_type];

  const handleSelectArtifactType = (newType: DdmArtifactType) => {
    const meta = DDM_ARTIFACT_TYPE_META[newType];
    setErrorMsg(null);
    setImageFile(null);
    setVideoFile(null);
    setGalleryFiles([]);
    setProbedVideoDuration(null);

    let defaultMediaMode: "none" | "image" | "video" = "none";
    if (newType === "gallery" || newType === "custom_banner") {
      defaultMediaMode = "image";
    }

    setValues((prev) => ({
      ...prev,
      artifact_type: newType,
      media_mode:
        !meta.allowsVideo && prev.media_mode === "video"
          ? defaultMediaMode
          : prev.media_mode === "none" && meta.requiresMedia
            ? defaultMediaMode
            : prev.media_mode,
      video_url: !meta.allowsVideo ? "" : prev.video_url,
      video_duration_seconds: !meta.allowsVideo
        ? null
        : prev.video_duration_seconds,
      duration_of_display:
        newType === "emergency_alert"
          ? null
          : prev.duration_of_display ?? 15,
    }));
  };

  const handleMediaModeChange = (mode: "none" | "image" | "video") => {
    setErrorMsg(null);
    if (mode === "none") {
      setImageFile(null);
      setVideoFile(null);
      setGalleryFiles([]);
      setProbedVideoDuration(null);
      setValues((prev) => ({
        ...prev,
        media_mode: "none",
        image_url: "",
        image_urls: [],
        video_url: "",
        video_duration_seconds: null,
      }));
    } else if (mode === "image") {
      // Mutual exclusion: clear video state
      setVideoFile(null);
      setProbedVideoDuration(null);
      setValues((prev) => ({
        ...prev,
        media_mode: "image",
        video_url: "",
        video_duration_seconds: null,
      }));
    } else if (mode === "video") {
      // Mutual exclusion: clear image state
      setImageFile(null);
      setGalleryFiles([]);
      const maxVidCap =
        values.artifact_type === "quote"
          ? MAX_QUOTE_VIDEO_DURATION_SECONDS
          : MAX_VIDEO_DURATION_SECONDS;
      setValues((prev) => ({
        ...prev,
        media_mode: "video",
        image_url: "",
        image_urls: [],
        duration_of_display:
          prev.duration_of_display && prev.duration_of_display > maxVidCap
            ? maxVidCap
            : prev.duration_of_display ?? maxVidCap,
      }));
    }
  };

  const handleImageFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    setErrorMsg(null);
    const files = Array.from(e.target.files ?? []);
    if (files.length === 0) return;

    if (values.artifact_type === "gallery") {
      const total = values.image_urls.length + galleryFiles.length + files.length;
      if (total > MAX_GALLERY_IMAGES) {
        setErrorMsg(
          `Gallery allows at most ${MAX_GALLERY_IMAGES} images (selected ${total}).`
        );
        return;
      }
      for (const f of files) {
        const err = validateImageFileClientSide(f);
        if (err) {
          setErrorMsg(err);
          return;
        }
      }
      setGalleryFiles((prev) => [...prev, ...files]);
      return;
    }

    const file = files[0];
    const err = validateImageFileClientSide(file);
    if (err) {
      setErrorMsg(err);
      return;
    }
    setImageFile(file);
    setVideoFile(null);
    setValues((prev) => ({
      ...prev,
      media_mode: "image",
      video_url: "",
      video_duration_seconds: null,
    }));
  };

  const handleVideoFileSelect = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setErrorMsg(null);
    const file = e.target.files?.[0];
    if (!file) return;

    const { error, durationSeconds } = await validateVideoFileClientSide(
      file,
      values.artifact_type
    );
    if (error) {
      setErrorMsg(error);
      return;
    }
    const maxVidCap =
      values.artifact_type === "quote"
        ? MAX_QUOTE_VIDEO_DURATION_SECONDS
        : MAX_VIDEO_DURATION_SECONDS;

    setVideoFile(file);
    setImageFile(null);
    setGalleryFiles([]);
    setProbedVideoDuration(durationSeconds ?? maxVidCap);
    setValues((prev) => ({
      ...prev,
      media_mode: "video",
      image_url: "",
      image_urls: [],
      video_duration_seconds: durationSeconds ?? maxVidCap,
      duration_of_display:
        prev.duration_of_display && prev.duration_of_display > maxVidCap
          ? maxVidCap
          : prev.duration_of_display ?? maxVidCap,
    }));
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg(null);

    const validationError = validateDdmFormSubmission(values, {
      hasImageFile: Boolean(imageFile),
      hasVideoFile: Boolean(videoFile),
      galleryFilesCount: galleryFiles.length,
    });
    if (validationError) {
      setErrorMsg(validationError);
      return;
    }

    const type = values.artifact_type;
    const contentPayload: Record<string, unknown> = {};

    if (type === "notice") {
      contentPayload.title = values.title.trim();
      contentPayload.body = values.body.trim();
      if (values.media_mode === "image" && values.image_url.trim()) {
        contentPayload.image_url = values.image_url.trim();
      }
      if (values.media_mode === "video" && values.video_url.trim()) {
        contentPayload.video_url = values.video_url.trim();
        contentPayload.video_duration_seconds =
          probedVideoDuration ?? values.video_duration_seconds ?? 15;
      }
    } else if (type === "birthday") {
      contentPayload.title = values.title.trim();
      contentPayload.body = values.body.trim();
      if (values.media_mode === "image" && values.image_url.trim()) {
        contentPayload.image_url = values.image_url.trim();
      }
    } else if (type === "emergency_alert") {
      contentPayload.title = values.title.trim();
      contentPayload.body = values.body.trim();
      if (values.media_mode === "image" && values.image_url.trim()) {
        contentPayload.image_url = values.image_url.trim();
      }
    } else if (type === "quote") {
      contentPayload.title = values.title.trim();
      contentPayload.body = values.body.trim();
      if (values.media_mode === "image" && values.image_url.trim()) {
        contentPayload.image_url = values.image_url.trim();
      }
      if (values.media_mode === "video" && values.video_url.trim()) {
        contentPayload.video_url = values.video_url.trim();
        contentPayload.video_duration_seconds =
          probedVideoDuration ?? values.video_duration_seconds ?? 5;
      }
    } else if (type === "gallery") {
      contentPayload.group_title = values.group_title.trim();
      contentPayload.image_urls = values.image_urls.filter((u) => u.trim());
    } else if (type === "custom_banner") {
      if (values.media_mode === "image" && values.image_url.trim()) {
        contentPayload.image_url = values.image_url.trim();
      }
      if (values.media_mode === "video" && values.video_url.trim()) {
        contentPayload.video_url = values.video_url.trim();
        contentPayload.video_duration_seconds =
          probedVideoDuration ?? values.video_duration_seconds ?? 15;
      }
    } else if (type === "event") {
      contentPayload.title = values.title.trim();
      contentPayload.body = values.body.trim();
      contentPayload.event_date = values.event_date.trim();
      if (values.media_mode === "image" && values.image_url.trim()) {
        contentPayload.image_url = values.image_url.trim();
      }
      if (values.media_mode === "video" && values.video_url.trim()) {
        contentPayload.video_url = values.video_url.trim();
        contentPayload.video_duration_seconds =
          probedVideoDuration ?? values.video_duration_seconds ?? 15;
      }
    }

    const startIso = new Date(values.start_timestamp).toISOString();
    const endIso = new Date(values.end_timestamp).toISOString();

    const payload: Record<string, unknown> = {
      device_id: values.device_id,
      artifact_type: type,
      start_timestamp: startIso,
      end_timestamp: endIso,
      remarks: values.remarks.trim(),
      content_payload: contentPayload,
    };

    if (type === "emergency_alert") {
      payload.pause_emergency_alert = values.pause_emergency_alert;
    } else {
      payload.duration_of_display = values.duration_of_display ?? 15;
      payload.per_cycle_display_count = values.per_cycle_display_count ?? 1;
    }

    if (type === "event") {
      payload.event_date = values.event_date.trim();
    }

    try {
      await onSubmit(
        {
          payload,
          imageFile,
          videoFile,
          galleryFiles,
          videoDurationSeconds:
            probedVideoDuration ?? values.video_duration_seconds,
        },
        editRecord?.id
      );
      onOpenChange(false);
    } catch (err) {
      setErrorMsg(
        err instanceof Error
          ? err.message
          : "Failed to save display artifact."
      );
    }
  };

  const hasVideoActive =
    values.media_mode === "video" &&
    (Boolean(videoFile) || Boolean(values.video_url.trim()));
  const maxDurationForCurrentState = hasVideoActive
    ? values.artifact_type === "quote"
      ? MAX_QUOTE_VIDEO_DURATION_SECONDS
      : MAX_VIDEO_DURATION_SECONDS
    : MAX_DISPLAY_DURATION_SECONDS;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92dvh] overflow-y-auto p-0 bg-card dark:bg-zinc-950 border-border/80 dark:border-zinc-800 rounded-2xl">
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-border/60 dark:border-zinc-800/80 text-left">
          <div className="flex items-center justify-between gap-2">
            <DialogTitle className="text-base sm:text-lg font-bold tracking-tight text-foreground">
              {isEdit
                ? `Edit ${currentMeta.label} Artifact (v${editRecord?.version ?? 1} → v${(editRecord?.version ?? 1) + 1})`
                : step === 1
                  ? "Step 1 · Choose Target DDM Device & Artifact Type"
                  : `Step 2 · Configure ${currentMeta.label} Artifact`}
            </DialogTitle>
            {!isEdit && (
              <span className="text-[11px] font-mono px-2.5 py-0.5 rounded-full bg-muted text-muted-foreground font-semibold">
                Step {step} of 2
              </span>
            )}
          </div>
          <DialogDescription className="text-xs text-muted-foreground mt-1">
            {isEdit
              ? "Saving changes increments the artifact version and triggers a signed real-time sync to the display device."
              : currentMeta.shortDescription}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleFormSubmit} className="p-6 space-y-5">
          {errorMsg && (
            <div
              role="alert"
              className="rounded-xl border border-red-500/25 bg-red-500/10 p-3.5 text-xs text-red-700 dark:text-red-300 flex items-start gap-2.5"
            >
              <AlertCircle className="h-4 w-4 shrink-0 mt-0.5 text-red-500" />
              <span className="leading-relaxed">{errorMsg}</span>
            </div>
          )}

          {/* STEP 1: Select Assigned DDM Device & Artifact Type */}
          {step === 1 && !isEdit && (
            <div className="space-y-5">
              {/* Target Device Selector */}
              <div className="space-y-2">
                <label
                  htmlFor="ddm-device-select"
                  className="text-xs font-semibold text-foreground flex items-center gap-1.5"
                >
                  <MonitorSmartphone className="h-3.5 w-3.5 text-muted-foreground" />
                  <span>1. Assigned Digital Display Module Device</span>
                </label>
                <select
                  id="ddm-device-select"
                  value={values.device_id}
                  onChange={(e) =>
                    setValues((prev) => ({
                      ...prev,
                      device_id: e.target.value,
                    }))
                  }
                  className="h-11 w-full rounded-xl border border-border/80 dark:border-zinc-800 bg-background dark:bg-zinc-900 px-3.5 text-xs sm:text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-zinc-950 dark:focus:ring-zinc-200"
                >
                  <option value="">Select an assigned DDM device...</option>
                  {devices.map((dev) => (
                    <option key={dev.device_id} value={dev.device_id}>
                      {dev.device_name} ({dev.registered_device_id})
                      {dev.location ? ` · ${dev.location}` : ""}
                    </option>
                  ))}
                </select>
              </div>

              {/* 7 Artifact Type Cards */}
              <div className="space-y-2">
                <span className="text-xs font-semibold text-foreground block">
                  2. Choose Display Artifact Type
                </span>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                  {DDM_ARTIFACT_TYPES.map((t) => {
                    const meta = DDM_ARTIFACT_TYPE_META[t];
                    const Icon = TYPE_ICONS[t];
                    const selected = values.artifact_type === t;
                    return (
                      <button
                        key={t}
                        type="button"
                        onClick={() => handleSelectArtifactType(t)}
                        className={cn(
                          "flex items-start gap-3 p-3.5 rounded-xl border text-left transition-all min-h-[68px]",
                          selected
                            ? "border-zinc-950 dark:border-zinc-100 bg-zinc-950/[0.04] dark:bg-white/[0.06] ring-1 ring-zinc-950 dark:ring-zinc-100"
                            : "border-border/70 dark:border-zinc-800 hover:bg-muted/40 dark:hover:bg-zinc-900/60"
                        )}
                      >
                        <div
                          className={cn(
                            "h-8 w-8 rounded-lg flex items-center justify-center shrink-0 mt-0.5",
                            selected
                              ? "bg-zinc-950 text-white dark:bg-zinc-100 dark:text-zinc-950"
                              : "bg-muted text-muted-foreground"
                          )}
                          style={
                            selected
                              ? {
                                  backgroundColor: "var(--color-brand-primary)",
                                  color: "var(--color-brand-foreground)",
                                }
                              : undefined
                          }
                        >
                          <Icon className="h-4 w-4" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center justify-between gap-1">
                            <span className="text-xs font-semibold text-foreground">
                              {meta.label}
                            </span>
                            {selected && (
                              <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
                            )}
                          </div>
                          <p className="text-[11px] text-muted-foreground line-clamp-2 mt-0.5 leading-snug">
                            {meta.shortDescription}
                          </p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* STEP 2: Artifact Content, Media (Image XOR Video), Schedule & Cycle */}
          {(step === 2 || isEdit) && (
            <div className="space-y-4">
              {/* Summary Bar showing selected device & type */}
              <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl bg-muted/40 dark:bg-zinc-900/60 border border-border/60 dark:border-zinc-800 px-3.5 py-2.5 text-xs">
                <div className="flex items-center gap-2">
                  <MonitorSmartphone className="h-4 w-4 text-muted-foreground" />
                  <span className="font-semibold text-foreground">
                    {devices.find((d) => d.device_id === values.device_id)
                      ?.device_name ||
                      editRecord?.device_name ||
                      values.device_id}
                  </span>
                  <span className="text-muted-foreground">·</span>
                  <span className="font-semibold text-foreground">
                    {currentMeta.label}
                  </span>
                </div>
                {!isEdit && (
                  <button
                    type="button"
                    onClick={() => setStep(1)}
                    className="text-[11px] font-medium underline underline-offset-2 text-muted-foreground hover:text-foreground"
                  >
                    Change type or device
                  </button>
                )}
              </div>

              {/* Dynamic Per-Type Text Fields with Live Character Counters */}
              {values.artifact_type === "gallery" ? (
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <label
                      htmlFor="ddm-group-title"
                      className="text-xs font-semibold text-foreground"
                    >
                      Gallery Group Title *
                    </label>
                    <span className="text-[10px] font-mono text-muted-foreground">
                      {values.group_title.length}/100
                    </span>
                  </div>
                  <input
                    id="ddm-group-title"
                    type="text"
                    maxLength={100}
                    placeholder="e.g., Annual Cardiac Wellness Camp"
                    value={values.group_title}
                    onChange={(e) =>
                      setValues((prev) => ({
                        ...prev,
                        group_title: e.target.value,
                      }))
                    }
                    className="h-10 w-full rounded-xl border border-border/80 dark:border-zinc-800 bg-background dark:bg-zinc-900 px-3.5 text-xs sm:text-sm text-foreground"
                  />
                </div>
              ) : (
                values.artifact_type !== "custom_banner" && (
                  <>
                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between">
                        <label
                          htmlFor="ddm-artifact-title"
                          className="text-xs font-semibold text-foreground"
                        >
                          {currentMeta.titleLabel || "Title"} *
                        </label>
                        {currentMeta.titleMaxLength && (
                          <span
                            className={cn(
                              "text-[11px] font-mono",
                              values.title.length > currentMeta.titleMaxLength
                                ? "text-red-500 font-semibold"
                                : "text-muted-foreground"
                            )}
                          >
                            {values.title.length}/{currentMeta.titleMaxLength}
                          </span>
                        )}
                      </div>
                      <input
                        id="ddm-artifact-title"
                        type="text"
                        placeholder={`Enter ${(
                          currentMeta.titleLabel || "title"
                        ).toLowerCase()}`}
                        value={values.title}
                        onChange={(e) =>
                          setValues((prev) => ({
                            ...prev,
                            title: e.target.value,
                          }))
                        }
                        className="h-10 w-full rounded-xl border border-border/80 dark:border-zinc-800 bg-background dark:bg-zinc-900 px-3.5 text-xs sm:text-sm text-foreground"
                      />
                    </div>

                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between">
                        <label
                          htmlFor="ddm-artifact-body"
                          className="text-xs font-semibold text-foreground"
                        >
                          {currentMeta.bodyLabel || "Message / Body"} *
                        </label>
                        {currentMeta.bodyMaxLength && (
                          <span
                            className={cn(
                              "text-[11px] font-mono",
                              values.body.length > currentMeta.bodyMaxLength
                                ? "text-red-500 font-semibold"
                                : "text-muted-foreground"
                            )}
                          >
                            {values.body.length}/{currentMeta.bodyMaxLength}
                          </span>
                        )}
                      </div>
                      <textarea
                        id="ddm-artifact-body"
                        rows={3}
                        placeholder={`Enter ${(
                          currentMeta.bodyLabel || "body"
                        ).toLowerCase()}`}
                        value={values.body}
                        onChange={(e) =>
                          setValues((prev) => ({
                            ...prev,
                            body: e.target.value,
                          }))
                        }
                        className="w-full rounded-xl border border-border/80 dark:border-zinc-800 bg-background dark:bg-zinc-900 p-3 text-xs sm:text-sm text-foreground"
                      />
                    </div>
                  </>
                )
              )}

              {/* Event Date field for Event artifact */}
              {values.artifact_type === "event" && (
                <div className="space-y-1.5">
                  <label
                    htmlFor="ddm-event-date"
                    className="text-xs font-semibold text-foreground"
                  >
                    Event Date (YYYY-MM-DD) *
                  </label>
                  <input
                    id="ddm-event-date"
                    type="date"
                    value={values.event_date}
                    onChange={(e) =>
                      setValues((prev) => ({
                        ...prev,
                        event_date: e.target.value,
                      }))
                    }
                    className="h-10 w-full rounded-xl border border-border/80 dark:border-zinc-800 bg-background dark:bg-zinc-900 px-3.5 text-xs sm:text-sm text-foreground"
                  />
                </div>
              )}

              {/* Strict Mutual Exclusion Media Selector (`None` | `Image` | `Video`) */}
              <div className="space-y-2.5 rounded-xl border border-border/70 dark:border-zinc-800 p-3.5 bg-muted/20 dark:bg-zinc-900/30">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="text-xs font-semibold text-foreground">
                    Media Attachment{" "}
                    {currentMeta.requiresMedia ? "(Required)" : "(Optional)"}
                  </span>
                  <span className="text-[10px] text-muted-foreground">
                    Image ≤ 4 MB ·{" "}
                    {currentMeta.allowsVideo
                      ? `Video ≤ 15 MB & ≤ ${currentMeta.maxVideoDurationSeconds ?? 15}s`
                      : "Images only"}
                  </span>
                </div>

                {values.artifact_type !== "gallery" && (
                  <div
                    role="group"
                    aria-label="Media Attachment Type"
                    className="grid grid-cols-3 gap-1.5 p-1 rounded-xl bg-muted/60 dark:bg-zinc-950 border border-border/60 dark:border-zinc-800"
                  >
                    {!currentMeta.requiresMedia && (
                      <button
                        type="button"
                        onClick={() => handleMediaModeChange("none")}
                        className={cn(
                          "flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-medium transition-all",
                          values.media_mode === "none"
                            ? "bg-card dark:bg-zinc-900 text-foreground shadow-xs font-semibold"
                            : "text-muted-foreground hover:text-foreground"
                        )}
                      >
                        <Ban className="h-3.5 w-3.5" />
                        <span>No Media</span>
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => handleMediaModeChange("image")}
                      className={cn(
                        "flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-medium transition-all",
                        values.media_mode === "image"
                          ? "bg-card dark:bg-zinc-900 text-foreground shadow-xs font-semibold"
                          : "text-muted-foreground hover:text-foreground"
                      )}
                    >
                      <ImageIcon className="h-3.5 w-3.5 text-sky-500" />
                      <span>Image Only</span>
                    </button>
                    {currentMeta.allowsVideo && (
                      <button
                        type="button"
                        onClick={() => handleMediaModeChange("video")}
                        className={cn(
                          "flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-medium transition-all",
                          values.media_mode === "video"
                            ? "bg-card dark:bg-zinc-900 text-foreground shadow-xs font-semibold"
                            : "text-muted-foreground hover:text-foreground"
                        )}
                      >
                        <VideoIcon className="h-3.5 w-3.5 text-violet-500" />
                        <span>Video Only</span>
                      </button>
                    )}
                  </div>
                )}

                {/* Gallery Multi-Image Upload / URLs (1..5 images) */}
                {values.artifact_type === "gallery" && (
                  <div className="space-y-2.5">
                    <label className="flex items-center justify-center gap-2 rounded-xl border border-dashed border-border dark:border-zinc-700 bg-background/60 dark:bg-zinc-950/60 px-4 py-3 text-xs font-medium text-foreground cursor-pointer hover:bg-muted/40 transition-colors">
                      <Upload className="h-4 w-4 text-muted-foreground" />
                      <span>
                        Upload Gallery Images (1 to 5 photos, max 4 MB each)
                      </span>
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        multiple
                        onChange={handleImageFileSelect}
                        className="sr-only"
                      />
                    </label>
                    {(values.image_urls.length > 0 ||
                      galleryFiles.length > 0) && (
                      <div className="flex flex-wrap gap-2">
                        {values.image_urls.map((u, idx) => (
                          <span
                            key={`url-${idx}`}
                            className="inline-flex items-center gap-1.5 rounded-lg bg-muted px-2.5 py-1 text-[11px]"
                          >
                            <span className="truncate max-w-[160px]">{u}</span>
                            <button
                              type="button"
                              onClick={() =>
                                setValues((prev) => ({
                                  ...prev,
                                  image_urls: prev.image_urls.filter(
                                    (_, i) => i !== idx
                                  ),
                                }))
                              }
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </span>
                        ))}
                        {galleryFiles.map((gf, idx) => (
                          <span
                            key={`file-${idx}`}
                            className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 px-2.5 py-1 text-[11px]"
                          >
                            <span className="truncate max-w-[160px]">
                              {gf.name}
                            </span>
                            <button
                              type="button"
                              onClick={() =>
                                setGalleryFiles((prev) =>
                                  prev.filter((_, i) => i !== idx)
                                )
                              }
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Single Image Upload or URL */}
                {values.artifact_type !== "gallery" &&
                  values.media_mode === "image" && (
                    <div className="space-y-2">
                      <label className="flex items-center justify-center gap-2 rounded-xl border border-dashed border-border dark:border-zinc-700 bg-background/60 dark:bg-zinc-950/60 px-4 py-2.5 text-xs font-medium text-foreground cursor-pointer hover:bg-muted/40 transition-colors">
                        <Upload className="h-4 w-4 text-sky-500" />
                        <span>
                          {imageFile
                            ? `Selected: ${imageFile.name} (${(
                                imageFile.size / 1024
                              ).toFixed(0)} KB)`
                            : "Choose Image File (JPEG, PNG, WEBP ≤ 4 MB)"}
                        </span>
                        <input
                          type="file"
                          accept="image/jpeg,image/png,image/webp"
                          onChange={handleImageFileSelect}
                          className="sr-only"
                        />
                      </label>
                      <input
                        type="url"
                        placeholder="Or paste an existing Storage Bucket / CDN image URL"
                        value={values.image_url}
                        onChange={(e) =>
                          setValues((prev) => ({
                            ...prev,
                            image_url: e.target.value,
                          }))
                        }
                        className="h-9 w-full rounded-lg border border-border/70 dark:border-zinc-800 bg-background dark:bg-zinc-900 px-3 text-xs text-foreground"
                      />
                    </div>
                  )}

                {/* Single Video Upload or URL */}
                {values.artifact_type !== "gallery" &&
                  values.media_mode === "video" &&
                  currentMeta.allowsVideo && (
                    <div className="space-y-2">
                      <label className="flex items-center justify-center gap-2 rounded-xl border border-dashed border-border dark:border-zinc-700 bg-background/60 dark:bg-zinc-950/60 px-4 py-2.5 text-xs font-medium text-foreground cursor-pointer hover:bg-muted/40 transition-colors">
                        <Upload className="h-4 w-4 text-violet-500" />
                        <span>
                          {videoFile
                            ? `Selected: ${videoFile.name} (${(
                                videoFile.size /
                                (1024 * 1024)
                              ).toFixed(2)} MB)`
                            : `Choose Video File (MP4, WEBM ≤ 15 MB & ≤ ${
                                currentMeta.maxVideoDurationSeconds ?? 15
                              }s)`}
                        </span>
                        <input
                          type="file"
                          accept="video/mp4,video/webm"
                          onChange={handleVideoFileSelect}
                          className="sr-only"
                        />
                      </label>
                      <input
                        type="url"
                        placeholder="Or paste an existing Storage Bucket / CDN video URL"
                        value={values.video_url}
                        onChange={(e) =>
                          setValues((prev) => ({
                            ...prev,
                            video_url: e.target.value,
                          }))
                        }
                        className="h-9 w-full rounded-lg border border-border/70 dark:border-zinc-800 bg-background dark:bg-zinc-900 px-3 text-xs text-foreground"
                      />
                    </div>
                  )}
              </div>

              {/* Schedule Window (Start & End Timestamps) */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <label
                    htmlFor="ddm-start-ts"
                    className="text-xs font-semibold text-foreground"
                  >
                    Start Timestamp *
                  </label>
                  <input
                    id="ddm-start-ts"
                    type="datetime-local"
                    value={values.start_timestamp}
                    onChange={(e) =>
                      setValues((prev) => ({
                        ...prev,
                        start_timestamp: e.target.value,
                      }))
                    }
                    className="h-10 w-full rounded-xl border border-border/80 dark:border-zinc-800 bg-background dark:bg-zinc-900 px-3 text-xs sm:text-sm text-foreground"
                  />
                </div>
                <div className="space-y-1.5">
                  <label
                    htmlFor="ddm-end-ts"
                    className="text-xs font-semibold text-foreground"
                  >
                    End Timestamp *
                  </label>
                  <input
                    id="ddm-end-ts"
                    type="datetime-local"
                    value={values.end_timestamp}
                    onChange={(e) =>
                      setValues((prev) => ({
                        ...prev,
                        end_timestamp: e.target.value,
                      }))
                    }
                    className="h-10 w-full rounded-xl border border-border/80 dark:border-zinc-800 bg-background dark:bg-zinc-900 px-3 text-xs sm:text-sm text-foreground"
                  />
                </div>
              </div>

              {/* Cycle Display Duration & Count OR Emergency Alert Pause Toggle */}
              {values.artifact_type === "emergency_alert" ? (
                <label className="flex items-center justify-between gap-3 rounded-xl border border-amber-500/30 bg-amber-500/10 p-3.5 cursor-pointer">
                  <div>
                    <div className="text-xs font-semibold text-foreground">
                      Pause Emergency Alert
                    </div>
                    <div className="text-[11px] text-muted-foreground">
                      When paused, the DDM device temporarily suspends rendering
                      this emergency alert without deleting it.
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    checked={values.pause_emergency_alert}
                    onChange={(e) =>
                      setValues((prev) => ({
                        ...prev,
                        pause_emergency_alert: e.target.checked,
                      }))
                    }
                    className="h-4 w-4 rounded border-zinc-400"
                  />
                </label>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <label
                      htmlFor="ddm-duration"
                      className="text-xs font-semibold text-foreground"
                    >
                      Duration of Display (1–{maxDurationForCurrentState}s) *
                    </label>
                    <input
                      id="ddm-duration"
                      type="number"
                      min={1}
                      max={maxDurationForCurrentState}
                      value={values.duration_of_display ?? 15}
                      onChange={(e) =>
                        setValues((prev) => ({
                          ...prev,
                          duration_of_display: Number(e.target.value) || 1,
                        }))
                      }
                      className="h-10 w-full rounded-xl border border-border/80 dark:border-zinc-800 bg-background dark:bg-zinc-900 px-3.5 text-xs sm:text-sm text-foreground"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label
                      htmlFor="ddm-cycle-count"
                      className="text-xs font-semibold text-foreground"
                    >
                      Per-Cycle Display Count (≥ 1) *
                    </label>
                    <input
                      id="ddm-cycle-count"
                      type="number"
                      min={1}
                      max={100}
                      value={values.per_cycle_display_count}
                      onChange={(e) =>
                        setValues((prev) => ({
                          ...prev,
                          per_cycle_display_count: Math.max(
                            1,
                            Number(e.target.value) || 1
                          ),
                        }))
                      }
                      className="h-10 w-full rounded-xl border border-border/80 dark:border-zinc-800 bg-background dark:bg-zinc-900 px-3.5 text-xs sm:text-sm text-foreground"
                    />
                  </div>
                </div>
              )}

              {/* Optional Remarks */}
              <div className="space-y-1.5">
                <label
                  htmlFor="ddm-remarks"
                  className="text-xs font-semibold text-foreground"
                >
                  Internal Remarks (Optional)
                </label>
                <input
                  id="ddm-remarks"
                  type="text"
                  placeholder="Optional internal note for operators"
                  value={values.remarks}
                  onChange={(e) =>
                    setValues((prev) => ({
                      ...prev,
                      remarks: e.target.value,
                    }))
                  }
                  className="h-10 w-full rounded-xl border border-border/80 dark:border-zinc-800 bg-background dark:bg-zinc-900 px-3.5 text-xs sm:text-sm text-foreground"
                />
              </div>
            </div>
          )}

          <DialogFooter className="flex-row items-center justify-end gap-2 pt-3 border-t border-border/60 dark:border-zinc-800/80">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="h-10 px-4 rounded-xl text-xs"
            >
              Cancel
            </Button>
            {step === 1 && !isEdit ? (
              <Button
                type="button"
                onClick={() => {
                  if (!values.device_id) {
                    setErrorMsg(
                      "Please select an assigned Digital Display Module device to continue."
                    );
                    return;
                  }
                  setErrorMsg(null);
                  setStep(2);
                }}
                style={{
                  backgroundColor: "var(--color-brand-primary)",
                  color: "var(--color-brand-foreground)",
                }}
                className="h-10 px-5 rounded-xl text-xs font-semibold bg-zinc-950 text-white dark:bg-zinc-100 dark:text-zinc-950"
              >
                Continue to Configuration →
              </Button>
            ) : (
              <Button
                type="submit"
                disabled={isSubmitting}
                style={{
                  backgroundColor: "var(--color-brand-primary)",
                  color: "var(--color-brand-foreground)",
                }}
                className="h-10 px-5 rounded-xl text-xs font-semibold bg-zinc-950 text-white dark:bg-zinc-100 dark:text-zinc-950 gap-1.5"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    <span>Saving & Signing...</span>
                  </>
                ) : isEdit ? (
                  <span>Save & Sync Update</span>
                ) : (
                  <span>Publish to Display</span>
                )}
              </Button>
            )}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

